import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { Product } from "./types";

export type CartLine = { product: Product; size: string | null; quantity: number };

type CartValue = {
  lines: CartLine[];
  count: number;
  subtotal: number;
  add: (product: Product, size: string | null, quantity?: number) => void;
  update: (productId: number, size: string | null, quantity: number) => void;
  remove: (productId: number, size: string | null) => void;
  clear: () => void;
};

const STORAGE_KEY = "annecosette_cart_v1";
const CartContext = createContext<CartValue | null>(null);

function key(productId: number, size: string | null) {
  return `${productId}:${size ?? "one-size"}`;
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as CartLine[];
    } catch {
      return [];
    }
  });

  useEffect(() => localStorage.setItem(STORAGE_KEY, JSON.stringify(lines)), [lines]);

  const add = useCallback((product: Product, size: string | null, quantity = 1) => {
    setLines((current) => {
      const lineKey = key(product.id, size);
      const existing = current.find((line) => key(line.product.id, line.size) === lineKey);
      if (!existing) return [...current, { product, size, quantity: Math.min(5, quantity) }];
      return current.map((line) =>
        key(line.product.id, line.size) === lineKey
          ? { ...line, product, quantity: Math.min(5, line.quantity + quantity) }
          : line,
      );
    });
  }, []);

  const remove = useCallback((productId: number, size: string | null) => {
    setLines((current) => current.filter((line) => key(line.product.id, line.size) !== key(productId, size)));
  }, []);

  const update = useCallback((productId: number, size: string | null, quantity: number) => {
    if (quantity <= 0) {
      remove(productId, size);
      return;
    }
    setLines((current) =>
      current.map((line) =>
        key(line.product.id, line.size) === key(productId, size)
          ? { ...line, quantity: Math.min(5, quantity) }
          : line,
      ),
    );
  }, [remove]);

  const clear = useCallback(() => setLines([]), []);

  const value = useMemo<CartValue>(
    () => ({
      lines,
      count: lines.reduce((sum, line) => sum + line.quantity, 0),
      subtotal: lines.reduce((sum, line) => sum + line.product.price_cents * line.quantity, 0),
      add,
      update,
      remove,
      clear,
    }),
    [add, clear, lines, remove, update],
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside CartProvider");
  return value;
}
